import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { subscribeToPushNotifications, unsubscribeFromPushNotifications } from '../../utils/pushNotifications';
import offlineManager from '../../utils/offlineManager';

export default function MobileLayout() {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useUI();
    const location = useLocation();
    const navigate = useNavigate();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(localStorage.getItem('stock_push_enabled') === 'true');
    const [warehouses, setWarehouses] = useState([]);
    const [queueCount, setQueueCount] = useState(0);
    const menuRef = useRef(null);

    useEffect(() => {
        const checkSubscription = async () => {
            if ('serviceWorker' in navigator && 'PushManager' in window) {
                try {
                    // Attendre que le SW soit prêt pour éviter les faux négatifs au démarrage
                    const registration = await navigator.serviceWorker.ready;
                    if (registration) {
                        const subscription = await registration.pushManager.getSubscription();
                        const isSubscribed = !!subscription;
                        setPushEnabled(isSubscribed);
                        // Mettre à jour le cache local pour le prochain démarrage
                        if (isSubscribed) {
                            localStorage.setItem('stock_push_enabled', 'true');
                        } else {
                            localStorage.removeItem('stock_push_enabled');
                        }
                    }
                } catch (err) {
                    console.error("Erreur lors de la vérification de l'abonnement push:", err);
                }
            }
        };
        checkSubscription();
    }, []);

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const res = await fetch(`/api/warehouses?companyId=${user.companyId}`);
                const data = await res.json();
                setWarehouses(data);
            } catch (err) { }
        };
        fetchWarehouses();

        // Check queue count periodically
        const checkQueue = async () => {
            const count = await offlineManager.getQueueCount();
            setQueueCount(count);
        };
        checkQueue();
        const interval = setInterval(checkQueue, 5000);
        return () => clearInterval(interval);
    }, [user.companyId]);

    const handleTogglePush = async () => {
        if (!user) return;

        if (pushEnabled) {
            const success = await unsubscribeFromPushNotifications();
            if (success) {
                setPushEnabled(false);
                localStorage.removeItem('stock_push_enabled');
            }
        } else {
            const success = await subscribeToPushNotifications(user.id, user.companyId);
            if (success) {
                setPushEnabled(true);
                localStorage.setItem('stock_push_enabled', 'true');
            }
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Map path to title
    const getPageTitle = () => {
        if (location.pathname === '/mobile') return 'Dashboard';
        if (location.pathname.includes('/scanner')) return 'Scanner & Saisie';
        if (location.pathname.includes('/transfer')) return 'Transfert de Stock';
        if (location.pathname.includes('/warehouses')) return 'Plan Entrepôt';
        return 'Stockizio Mobile';
    };

    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const [showNavbar, setShowNavbar] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    // Scroll behavior: hide navbar on scroll down, show on scroll up
    useEffect(() => {
        let scY = window.scrollY;
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            // Toujours afficher en haut de page ou si le scroll est minime
            if (currentScrollY < 20) {
                setShowNavbar(true);
            } else if (Math.abs(currentScrollY - scY) > 5) {
                // Si on descend, on cache. Si on monte, on affiche.
                setShowNavbar(currentScrollY < scY);
            }

            scY = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 min-h-screen flex flex-col">
            {/* Header */}
            <header className="sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md px-4 py-3">
                <div className="flex items-center justify-between max-w-2xl mx-auto">
                    <div className="flex items-center gap-3">
                        <div className="bg-white rounded-xl p-1.5 shadow-sm flex items-center justify-center border border-slate-200 dark:border-white/10">
                            <img src="/Stockizio.png" alt="Stockizio" className="h-7 w-auto object-contain" />
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={toggleTheme}
                            className="p-1.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors flex items-center justify-center text-slate-500 dark:text-slate-400"
                        >
                            <span className="material-symbols-outlined">
                                {theme === 'dark' ? 'light_mode' : 'dark_mode'}
                            </span>
                        </button>

                        {'Notification' in window && (
                            <button
                                onClick={handleTogglePush}
                                className={`p-1.5 rounded-full transition-colors relative ${pushEnabled ? 'text-primary bg-primary/10' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800'}`}
                                title={pushEnabled ? 'Désactiver les notifications push' : 'Activer les notifications push'}
                            >
                                <span className="material-symbols-outlined">
                                    {pushEnabled ? 'notifications_active' : 'notifications'}
                                </span>
                                {!pushEnabled && (
                                    <span className="absolute top-1 right-1 flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500 border border-white dark:border-slate-900"></span>
                                    </span>
                                )}
                            </button>
                        )}

                        {queueCount > 0 && (
                            <button
                                onClick={() => navigate('/mobile/settings')}
                                className="p-1.5 rounded-full bg-rose-500 text-white animate-pulse flex items-center justify-center shadow-lg shadow-rose-500/20"
                                title={`${queueCount} éléments à synchroniser`}
                            >
                                <span className="material-symbols-outlined text-[18px]">sync</span>
                            </button>
                        )}

                        <div className="relative" ref={menuRef}>
                            <button
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                className="p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-slate-900 relative"
                            >
                                {user?.avatar ? (
                                    <img src={user.avatar} alt="Profil" className="w-8 h-8 rounded-full object-cover border border-slate-200 dark:border-slate-700" />
                                ) : (
                                    <span className="material-symbols-outlined text-[32px] text-slate-500">account_circle</span>
                                )}
                                <div className={`absolute bottom-1 right-1 size-2.5 rounded-full border-2 border-white dark:border-slate-900 shadow-sm transition-colors ${isOnline ? 'bg-emerald-500' : 'bg-rose-500'}`} title={isOnline ? 'En ligne' : 'Hors-ligne'}></div>
                            </button>

                            {/* Dropdown Menu */}
                            {showUserMenu && (
                                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-2 animate-in fade-in zoom-in-95 duration-100 z-50">
                                    <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700 mb-1">
                                        <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{user?.name}</p>
                                        <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                                    </div>
                                    <button
                                        onClick={() => { setShowUserMenu(false); navigate('/mobile/settings'); }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">settings</span>
                                        Configuration
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors mt-1 border-t border-slate-100 dark:border-slate-700 pt-2"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">logout</span>
                                        Déconnexion
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 w-full max-w-2xl mx-auto p-4 space-y-6 pb-24">
                <Outlet />
            </main>

            {/* Bottom Navigation Bar */}
            <nav className={`fixed bottom-0 inset-x-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-6 pt-3 pb-[calc(env(safe-area-inset-bottom,16px)+24px)] flex items-center justify-between z-20 shadow-[0_-8px_20px_rgba(0,0,0,0.08)] transition-transform duration-300 ${showNavbar ? 'translate-y-0' : 'translate-y-full'}`}>
                <NavLink
                    to="/mobile"
                    end
                    className={({ isActive }) => `flex flex-col items-center gap-1 ${isActive ? 'text-primary' : 'text-slate-400 dark:text-slate-500'}`}
                >
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>dashboard</span>
                    <span className="text-[10px] font-bold uppercase tracking-tight">Accueil</span>
                </NavLink>

                <NavLink
                    to="/mobile/warehouses"
                    className={({ isActive }) => `flex flex-col items-center gap-1 ${isActive ? 'text-primary' : 'text-slate-400 dark:text-slate-500'}`}
                >
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>map</span>
                    <span className="text-[10px] font-bold uppercase tracking-tight">Plan</span>
                </NavLink>

                <NavLink
                    to="/mobile/scanner"
                    className={({ isActive }) => `flex flex-col items-center gap-1 ${isActive ? 'text-primary' : 'text-slate-400 dark:text-slate-500'}`}
                >
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>qr_code</span>
                    <span className="text-[10px] font-bold uppercase tracking-tight">Scanner</span>
                </NavLink>

                <NavLink
                    to="/mobile/orders"
                    className={({ isActive }) => `flex flex-col items-center gap-1 ${isActive ? 'text-primary' : 'text-slate-400 dark:text-slate-500'}`}
                >
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>inventory</span>
                    <span className="text-[10px] font-bold uppercase tracking-tight">Commandes</span>
                </NavLink>

                <NavLink
                    to="/mobile/settings"
                    className={({ isActive }) => `flex flex-col items-center gap-1 relative ${isActive ? 'text-primary' : 'text-slate-400 dark:text-slate-500'}`}
                >
                    <span className="material-symbols-outlined">settings</span>
                    <span className="text-[10px] font-bold uppercase tracking-tight">Reglages</span>
                    {queueCount > 0 && (
                        <span className="absolute -top-1 right-2 w-4 h-4 bg-rose-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white dark:border-slate-800">
                            {queueCount}
                        </span>
                    )}
                </NavLink>
            </nav>

            <style>{`
                .pb-safe {
                    padding-bottom: calc(env(safe-area-inset-bottom, 16px) + 24px);
                }
            `}</style>
        </div>
    );
}
